/**
 * Date utilities for homework distribution
 */

/**
 * Returns the upcoming Monday as a Date object.
 * If today is Monday, returns today.
 */
export function getUpcomingMonday(): Date {
  const today = new Date()
  const dayOfWeek = today.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  let daysUntilMonday: number
  if (dayOfWeek === 1) {
    // Today is Monday
    daysUntilMonday = 0
  } else if (dayOfWeek === 0) {
    // Sunday - next Monday is tomorrow
    daysUntilMonday = 1
  } else {
    // Tuesday-Saturday - calculate days until next Monday
    daysUntilMonday = 8 - dayOfWeek
  }

  const monday = new Date(today)
  monday.setDate(today.getDate() + daysUntilMonday)
  // Reset time to midnight for consistent date comparisons
  monday.setHours(0, 0, 0, 0)
  return monday
}

/**
 * Returns the Saturday after the upcoming Monday as a Date object.
 */
export function getUpcomingSaturday(): Date {
  const monday = getUpcomingMonday()
  const saturday = new Date(monday)
  saturday.setDate(monday.getDate() + 5) // Monday + 5 = Saturday
  return saturday
}

/**
 * Returns an array of Date objects for each day in the range (inclusive).
 * @param start - Start date
 * @param end - End date
 * @returns Array of Date objects, one for each day in the range
 */
export function getDaysInRange(start: Date, end: Date): Date[] {
  const days: Date[] = []
  const current = new Date(start)
  current.setHours(0, 0, 0, 0)

  const endNormalized = new Date(end)
  endNormalized.setHours(0, 0, 0, 0)

  while (current <= endNormalized) {
    days.push(new Date(current))
    current.setDate(current.getDate() + 1)
  }

  return days
}

/**
 * Distributes items evenly across days, biasing extras toward the start.
 *
 * @param itemCount - Total number of items to distribute
 * @param dayCount - Number of days to distribute across
 * @returns Array of counts, one per day, with higher counts at the start
 *
 * @example
 * distributeItemsAcrossDays(15, 6) // [3, 3, 3, 2, 2, 2] - extras at start
 * distributeItemsAcrossDays(17, 6) // [3, 3, 3, 3, 3, 2]
 * distributeItemsAcrossDays(6, 6)  // [1, 1, 1, 1, 1, 1]
 * distributeItemsAcrossDays(3, 6)  // [1, 1, 1, 0, 0, 0]
 */
export function distributeItemsAcrossDays(
  itemCount: number,
  dayCount: number,
): number[] {
  if (dayCount <= 0) return []
  if (itemCount <= 0) return Array(dayCount).fill(0)

  const basePerDay = Math.floor(itemCount / dayCount)
  const extras = itemCount % dayCount

  // Create array with base count, then add extras to the first days
  const distribution: number[] = []
  for (let i = 0; i < dayCount; i++) {
    distribution.push(basePerDay + (i < extras ? 1 : 0))
  }

  return distribution
}

/**
 * Formats a Date object as an ISO date string (YYYY-MM-DD).
 * Uses local timezone components to avoid UTC conversion issues.
 * @param date - Date to format
 * @returns ISO date string in local timezone
 */
export function formatDateISO(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Parses an ISO date string (YYYY-MM-DD) into a Date object.
 * @param dateStr - ISO date string
 * @returns Date object set to midnight local time
 */
export function parseDateISO(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  date.setHours(0, 0, 0, 0)
  return date
}
